import { useId, useRef, useState, type FormEvent } from "react";
import {
  E164_PATTERN,
  type WhatsAppAgileSignup,
  type WhatsAppChannelCode,
  type WhatsAppChannelsSettings,
  type WhatsAppMetaSignup,
  type WhatsAppSenderNotReadyReason,
  type WhatsAppSenderPerson,
  type WhatsAppSenderView,
} from "@gioia365/contracts";
import { storedChannelDownHolds } from "@gioia365/domain/whatsapp";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { StatusPill } from "../../design/components/StatusPill";
import { TextField } from "../../design/components/TextField";
import type { Tone } from "../../design/components/tone";
import { messageForError } from "../../i18n/errors";
import { deviceTimeZone, formatNumericDateTime } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { formatPhoneForDisplay } from "../../leads/lead-display";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { useSetWhatsAppSender, whatsAppChannelsQuery, whatsAppSendersQuery } from "../../whatsapp/settings-api";
import type { Feedback } from "./draft-dialogs";
import { AgileSetupFields, useAgileSetup } from "./whatsapp-agile";
import { MetaRegistrationNote, MetaSenderStatus, useMetaRegistration, type MetaFeedback } from "./whatsapp-meta";
import "./whatsapp-settings.css";

// Il WhatsApp di una persona (piano M6, R1, Task 5a; D2, D4; questioni 404 e 405; mock-up `whatsapp.html`, `#m-wa-setup`, e
// `mockup.js`, `bindWaSetup`, `stateOf` e la cella «WhatsApp» del Team): i nomi dei tre WhatsApp, lo stato di una riga, la cella
// della colonna «WhatsApp» del Team e la finestra «Imposta», che «Invio dei WhatsApp» e il Team aprono allo stesso modo.
//
// Nella finestra si sceglie fra i WhatsApp che l'azienda usa (`GET /api/settings/whatsapp-channels`, D2) e che il server può usare
// (`channelBlock`): «A mano» sempre; il WhatsApp ufficiale soltanto con le variabili di Meta sul server (senza: «Il WhatsApp ufficiale non
// è ancora collegato a Gioia365»); da R9 (Task 27b) WhatsApp Agile soltanto con la sua chiave sul server (senza: «Non disponibile: …»,
// `agileSignup.available`); uno che l'azienda non usa con il perché del mock-up. Con Agile la finestra chiede anche il codice, apre il
// codice QR del fornitore e salva con «Ho inquadrato il codice, salva» (`whatsapp-agile.tsx`); lo stato di una riga di Agile viene
// dall'ultimo controllo salvato del suo canale (`agileHealth`, con la regola dei 10 minuti del dominio, `storedChannelDownHolds`).
// Con il WhatsApp ufficiale (R2, Task 7b) il pulsante è «Registra su Meta e salva» (`whatsapp-meta.tsx`); una persona senza un
// WhatsApp parte con l'ufficiale se l'azienda lo usa (mock-up: «Un Commerciale nuovo parte con il WhatsApp ufficiale, il
// consigliato»). Il numero si ripulisce come nel mock-up (spazi, punti,
// trattini e parentesi via, «00» davanti diventa «+») e deve avere il prefisso: da 8 a 15 cifre (la regola del mock-up), la prima
// non zero (quella del server). Un numero che è già di un collega lo dice il campo, con il suo nome (`409 whatsapp_number_taken`,
// `details.holderDisplayName`); un codice di Agile già di un collega lo dice il campo del codice (`details.field: "agileCode"`). La
// versione che il salvataggio porta è quella
// del WhatsApp com'era quando la finestra si è aperta, anche se intanto l'elenco si rilegge (al ritorno sulla scheda del browser):
// un WhatsApp cambiato nel frattempo da un altro risponde `409 version_conflict`, la finestra lo dice e il salvataggio dopo porta la
// versione di adesso. Chi può impostare chi lo decide il server (`canSet`): qui soltanto si mostra.

/** I tre WhatsApp nell'ordine del mock-up (`WA_KINDS`). */
export const WHATSAPP_CHANNEL_ORDER: readonly WhatsAppChannelCode[] = ["meta", "agile", "manual"];

/**
 * I WhatsApp che esistono nel codice in questo rilascio (WA-2, questione 404): uno che non c'è ancora si vede spento, «Arriva con il
 * prossimo rilascio». Da R9 ci sono tutti e tre.
 */
export const CHANNELS_IN_RELEASE: ReadonlySet<WhatsAppChannelCode> = new Set(["manual", "meta", "agile"]);

export function inRelease(channel: WhatsAppChannelCode): boolean {
  return CHANNELS_IN_RELEASE.has(channel);
}

/**
 * Perché un WhatsApp non si sceglie per una persona: non c'è ancora in questo rilascio, il WhatsApp ufficiale non è collegato a
 * Gioia365 (mancano le variabili di Meta sul server), WhatsApp Agile non è disponibile (manca la sua chiave sul server), oppure
 * l'azienda non lo usa.
 */
export type ChannelBlock = "next_release" | "meta_unavailable" | "agile_unavailable" | "not_used";

/** Ciò che la finestra legge della scelta dell'azienda: i due WhatsApp automatici e se il server li può usare. */
export type CompanyChannels = Pick<WhatsAppChannelsSettings, "meta" | "agile"> & {
  metaSignup: Pick<WhatsAppMetaSignup, "available">;
  agileSignup: Pick<WhatsAppAgileSignup, "available">;
};

/**
 * Se un WhatsApp si sceglie nella finestra «Imposta» (D2, questioni 391 e 404): «A mano» sempre; gli altri soltanto se esistono in
 * questo rilascio, il WhatsApp ufficiale soltanto con le variabili di Meta, Agile soltanto con la sua chiave (R9), e se l'azienda li
 * usa. Finché la scelta dell'azienda non è arrivata (`null`), soltanto «A mano».
 */
export function channelBlock(channel: WhatsAppChannelCode, company: CompanyChannels | null, release: ReadonlySet<WhatsAppChannelCode> = CHANNELS_IN_RELEASE): ChannelBlock | null {
  if (channel === "manual") return null;
  if (!release.has(channel)) return "next_release";
  if (company === null) return "not_used";
  if (channel === "meta" && !company.metaSignup.available) return "meta_unavailable";
  if (channel === "agile" && !company.agileSignup.available) return "agile_unavailable";
  return company[channel] ? null : "not_used";
}

/** Il WhatsApp di una persona: senza una riga scrive a mano. */
export function channelOf(person: WhatsAppSenderPerson): WhatsAppChannelCode {
  return person.sender?.channel ?? "manual";
}

/**
 * `team`: la seconda riga della cella del Team; `null` a mano, dove il modo in grassetto dice già tutto (rilievo del product owner su
 * R1). `values`: le ore che le parole di WhatsApp Agile dicono (da quando non consegna, l'ultimo controllo).
 */
type StateLook = { tone: Tone; icon: "check" | "clock" | "minus" | "alert"; page: MessageKey; team: MessageKey | null; values?: Record<string, string> };

/** L'ora con cui si legge lo stato salvato del canale di WhatsApp Agile, e il fuso dell'azienda in cui si dicono le ore. */
export type StateClock = { now: Date; timeZone: string };

/** Perché un WhatsApp ufficiale non è pronto (`notReadyReason`, D9), nella pagina e nella cella del Team. */
const META_NOT_READY: Record<WhatsAppSenderNotReadyReason, StateLook> = {
  failed: { tone: "danger", icon: "alert", page: "whatsappSendingSettings.states.metaFailed", team: "whatsappSendingSettings.team.metaFailed" },
  caller_removed: { tone: "warning", icon: "alert", page: "whatsappSendingSettings.states.metaCallerRemoved", team: "whatsappSendingSettings.team.metaCallerRemoved" },
  not_connected: { tone: "warning", icon: "clock", page: "whatsappSendingSettings.states.metaNotConnected", team: "whatsappSendingSettings.team.metaNotConnected" },
  meta_not_configured: { tone: "warning", icon: "minus", page: "whatsappSendingSettings.states.metaNotConfigured", team: "whatsappSendingSettings.team.metaNotConfigured" },
};

/**
 * Lo stato di una riga (`stateOf` e la cella del Team del mock-up): a mano, oppure pronto o no presso il fornitore. Il WhatsApp
 * ufficiale non pronto dice il perché del server (`notReadyReason`): non riuscito, il cellulare tolto dal parco, il collegamento con
 * Meta da finire, le variabili di Meta che mancano.
 */
export function stateLook(sender: WhatsAppSenderView | null, clock?: StateClock): StateLook {
  const channel = sender?.channel ?? "manual";
  if (channel === "manual") return { tone: "neutral", icon: "minus", page: "whatsappSendingSettings.states.manual", team: null };
  const ready = sender?.state === "ready";
  if (channel === "meta") {
    const reason = sender?.notReadyReason ?? (sender?.state === "failed" ? "failed" : null);
    if (reason !== null) return META_NOT_READY[reason];
    return ready
      ? { tone: "success", icon: "check", page: "whatsappSendingSettings.states.metaReady", team: "whatsappSendingSettings.team.metaReady" }
      : { tone: "warning", icon: "clock", page: "whatsappSendingSettings.states.metaPending", team: "whatsappSendingSettings.team.metaPending" };
  }
  if (!ready) return { tone: "warning", icon: "clock", page: "whatsappSendingSettings.states.agilePending", team: "whatsappSendingSettings.team.agilePending" };
  return agileLook(sender?.agileHealth ?? null, clock ?? { now: new Date(), timeZone: deviceTimeZone() });
}

/**
 * Un WhatsApp Agile pronto, dall'ultimo controllo salvato del suo canale (piano M6, R9, Task 27b; `agileHealth`): consegna, mai
 * controllato, oppure non consegna. Un «giù» di meno di 10 minuti tiene i messaggi a mano (la regola del dominio, la stessa della
 * finestra del WhatsApp); uno più vecchio dice l'ora del controllo, perché il prossimo invio chiede di nuovo al fornitore.
 */
function agileLook(health: WhatsAppSenderView["agileHealth"] | null, clock: StateClock): StateLook {
  if (health === null || health === undefined) {
    return { tone: "neutral", icon: "clock", page: "whatsappSendingSettings.states.agileUnchecked", team: "whatsappSendingSettings.team.agileUnchecked" };
  }
  if (health.healthy) return { tone: "success", icon: "check", page: "whatsappSendingSettings.states.agileReady", team: "whatsappSendingSettings.team.agileReady" };
  const checkedAt = new Date(health.checkedAt);
  if (storedChannelDownHolds({ healthy: false, checkedAt }, clock.now)) {
    const since = formatNumericDateTime(clock.timeZone, health.downSince ?? health.checkedAt);
    return { tone: "danger", icon: "alert", page: "whatsappSendingSettings.states.agileDown", team: "whatsappSendingSettings.team.agileDown", values: { since } };
  }
  const checked = formatNumericDateTime(clock.timeZone, health.checkedAt);
  return { tone: "warning", icon: "alert", page: "whatsappSendingSettings.states.agileDownStale", team: "whatsappSendingSettings.team.agileDownStale", values: { checked } };
}

/** L'ora di adesso e il fuso dell'azienda di chi guarda, per lo stato di WhatsApp Agile. */
function useStateClock(): StateClock {
  const timeZone = useAccess().context.settings.timeZone;
  return { now: new Date(), timeZone };
}

/** Il numero di una persona come si legge, o il segno vuoto con «Nessun numero» per chi legge lo schermo. */
export function SenderNumber({ sender }: { sender: WhatsAppSenderView | null }) {
  const t = useT();
  if (!sender) {
    return (
      <>
        <span aria-hidden="true">—</span>
        <span className="g-visually-hidden">{t("whatsappSendingSettings.people.noNumber")}</span>
      </>
    );
  }
  return <span className="g-wa-number">{formatPhoneForDisplay(sender.e164)}</span>;
}

/**
 * La cella «WhatsApp» del Team (mock-up `waCell`): il WhatsApp in breve, in grassetto, il numero e lo stato piccolo; «Imposta» per
 * chi il server lascia impostare (`canSet`). A mano lo stato non c'è: il mock-up ripeteva «a mano» sotto «A mano» (rilievo del
 * product owner su R1, 6 ottobre 2026).
 */
export function WhatsAppTeamCell({ person, onSetup }: { person: WhatsAppSenderPerson; onSetup: () => void }) {
  const t = useT();
  const look = stateLook(person.sender, useStateClock());
  return (
    <span className="g-wa-cell">
      <strong>{t(`whatsappSendingSettings.channels.${channelOf(person)}.short`)}</strong>
      {person.sender ? <span className="g-wa-number">{formatPhoneForDisplay(person.sender.e164)}</span> : null}
      {look.team === null ? null : (
        <span className="g-table__sub" data-tone={look.tone}>
          {t(look.team, look.values)}
        </span>
      )}
      {person.canSet ? (
        <Button variant="ghost" onClick={onSetup} aria-label={t("whatsappSendingSettings.people.setupNamed", { name: person.displayName })}>
          {t("whatsappSendingSettings.people.setup")}
        </Button>
      ) : null}
    </span>
  );
}

/** Lo stato di una riga di «Invio dei WhatsApp», con testo e icona (mai il solo colore). */
export function SenderState({ sender }: { sender: WhatsAppSenderView | null }) {
  const t = useT();
  const look = stateLook(sender, useStateClock());
  return (
    <StatusPill tone={look.tone} icon={look.icon}>
      {t(look.page, look.values)}
    </StatusPill>
  );
}

/** Spazi, punti, trattini e parentesi via; «00» davanti diventa «+» (`cleanWaNumber` del mock-up). */
export function cleanWhatsAppNumber(value: string): string {
  const raw = value.replace(/[\s.\-()]/g, "");
  return raw.startsWith("00") ? `+${raw.slice(2)}` : raw;
}

// Il prefisso e da 8 a 15 cifre, come nel mock-up; la prima cifra non è zero, come vuole il server (`E164_PATTERN`).
const WHATSAPP_NUMBER = /^\+\d{8,15}$/;

export function isWhatsAppNumber(cleaned: string): boolean {
  return WHATSAPP_NUMBER.test(cleaned) && E164_PATTERN.test(cleaned);
}

/** Il nome di una persona come lo dice il mock-up in «Cosa cambia:»: la prima parola del nome. */
function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName;
}

/** Il rifiuto del numero già di un collega, con il suo nome quando il server lo dice. */
function numberTakenText(t: Translate, error: ApiRequestError): string {
  const holder = error.details?.holderDisplayName;
  return typeof holder === "string" && holder.trim() !== "" ? t("whatsappSendingSettings.dialog.numberTaken", { name: holder }) : messageForError(t, error);
}

/** Il rifiuto del codice di WhatsApp Agile già di un collega (`details.field: "agileCode"`), con il suo nome quando il server lo dice. */
function codeTakenText(t: Translate, error: ApiRequestError): string {
  const holder = error.details?.holderDisplayName;
  return typeof holder === "string" && holder.trim() !== "" ? t("whatsappSendingSettings.agile.codeTaken", { name: holder }) : messageForError(t, error);
}

/** Il pulsante della finestra «Imposta» per ogni WhatsApp (mock-up `bindWaSetup`). */
const SAVE_LABEL: Record<WhatsAppChannelCode, MessageKey> = {
  meta: "whatsappSendingSettings.dialog.saveMeta",
  agile: "whatsappSendingSettings.dialog.saveAgile",
  manual: "whatsappSendingSettings.dialog.save",
};

type DialogProps = {
  tab: TabContext;
  /** La persona com'era all'apertura: la finestra segue poi la sua riga nell'elenco riletto (la versione dopo un conflitto). */
  person: WhatsAppSenderPerson;
  onClose: () => void;
  /** Salvato: l'avviso da mostrare dopo la chiusura. */
  onSaved: (message: string) => void;
};

/** La finestra «Imposta»: quale WhatsApp usa la persona e il numero da cui scrive (o che registra su Meta). */
export function WhatsAppSenderDialog({ tab, person: opened, onClose, onSaved }: DialogProps) {
  const t = useT();
  const ids = useId();
  const formId = `${ids}-form`;
  const access = useAccess();
  const organization = access.context.organization.name;
  const senders = useQuery(whatsAppSendersQuery(tab));
  const company = useQuery(whatsAppChannelsQuery(tab)).data ?? null;
  const person = senders.data?.people.find((candidate) => candidate.membershipId === opened.membershipId) ?? opened;
  const save = useSetWhatsAppSender(tab);
  /** Perché un WhatsApp non si sceglie per questa persona; il WhatsApp ufficiale anche quando il server non la lascia registrare. */
  const blockOf = (option: WhatsAppChannelCode): ChannelBlock | "not_allowed" | null =>
    channelBlock(option, company) ?? (option === "meta" && !person.canRegisterMeta ? "not_allowed" : null);
  // La scelta fatta qui; finché non c'è, il WhatsApp della persona, o per chi non ne ha uno l'ufficiale se si può (il consigliato).
  const [chosen, setChosen] = useState<WhatsAppChannelCode | null>(() => opened.sender?.channel ?? null);
  const preferred: WhatsAppChannelCode = chosen ?? (blockOf("meta") === null ? "meta" : "manual");
  // Un WhatsApp che non si sceglie (l'azienda non lo usa, o la sua scelta non è ancora arrivata) lascia «A mano».
  const channel: WhatsAppChannelCode = blockOf(preferred) === null ? preferred : "manual";
  const [number, setNumber] = useState(() => (opened.sender ? formatPhoneForDisplay(opened.sender.e164) : ""));
  // La versione del WhatsApp com'era all'apertura (`null`: la persona non ne aveva uno): il salvataggio porta questa, non quella di
  // una rilettura arrivata nel frattempo, così una modifica di un altro risponde `409` invece di sparire sotto questa.
  const baseVersion = useRef<number | null>(opened.sender?.version ?? null);
  const [numberError, setNumberError] = useState<Feedback | null>(null);
  const [problem, setProblem] = useState<Feedback | null>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const firstName = firstNameOf(person.displayName);
  const codeRef = useRef<HTMLInputElement>(null);
  // Il codice QR salvato lo legge soltanto chi ha «Invio dei WhatsApp» (o Gioia365): come il server (`agile/pairing.ts`).
  const canRepair = access.isPlatform || access.can("settings.whatsapp_sending");
  const agile = useAgileSetup({ tab, person, pairingBaseUrl: company?.agileSignup.pairingBaseUrl ?? null, codeRef, canRepair });
  let effect: string;
  if (channel === "meta") effect = t("whatsappSendingSettings.dialog.effectMeta", { name: firstName });
  else if (channel === "agile") effect = t("whatsappSendingSettings.dialog.effectAgile", { name: firstName });
  else effect = t("whatsappSendingSettings.dialog.effectManual", { name: firstName });

  function feedback(next: MetaFeedback | null) {
    if (next === null) {
      setNumberError(null);
      setProblem(null);
    } else if (next.field === "number") {
      setNumberError(next);
      numberRef.current?.focus();
    } else {
      setProblem(next);
    }
  }

  const meta = useMetaRegistration({
    tab,
    showing: channel === "meta",
    person,
    people: senders.data?.people ?? null,
    signup: company?.metaSignup ?? null,
    baseVersionRef: baseVersion,
    onFeedback: feedback,
    onSaved: () => onSaved(t("whatsappSendingSettings.dialog.saved", { effect: t("whatsappSendingSettings.dialog.effectMeta", { name: firstName }) })),
  });
  const busy = save.isPending || meta.busy;
  // Si chiude anche mentre si aspetta la finestra di Meta (l'attesa si abbandona), non mentre il server salva.
  const closable = !save.isPending && meta.closable;
  function close() {
    if (!closable) return;
    meta.abandon();
    onClose();
  }

  /** Il numero scritto, ripulito; `null` (con l'errore sul campo) se non ha il prefisso. */
  function validNumber(): string | null {
    const e164 = cleanWhatsAppNumber(number);
    if (!isWhatsAppNumber(e164)) {
      setNumberError({ text: (tr) => tr("whatsappSendingSettings.dialog.numberInvalid") });
      numberRef.current?.focus();
      return null;
    }
    setNumberError(null);
    return e164;
  }

  /**
   * «Salva» a mano, o «Ho inquadrato il codice, salva» con WhatsApp Agile (`agileCode`): il server chiede al fornitore se il codice è
   * di quel numero e se il telefono consegna, e ogni rifiuto ha il suo posto: il codice che non riconosce sul campo del codice, il
   * telefono non collegato e il fornitore che non risponde sotto, con le loro parole.
   */
  async function saveSender(e164: string, agileCode?: string) {
    setProblem(null);
    const expectedVersion = baseVersion.current;
    try {
      await save.mutateAsync({
        membershipId: person.membershipId,
        body: { channel, e164, ...(agileCode === undefined ? {} : { agileCode }), ...(expectedVersion === null ? {} : { expectedVersion }) },
      });
      onSaved(t("whatsappSendingSettings.dialog.saved", { effect }));
    } catch (error) {
      const fields = error instanceof ApiRequestError && error.code === "validation_failed" && Array.isArray(error.details?.fields) ? error.details.fields : [];
      if (error instanceof ApiRequestError && error.code === "whatsapp_agile_code_not_recognised") {
        agile.setCodeError({ text: (tr) => messageForError(tr, error) });
        codeRef.current?.focus();
      } else if (fields.includes("agileCode")) {
        agile.setCodeError({ text: (tr) => tr("whatsappSendingSettings.agile.codeInvalid") });
        codeRef.current?.focus();
      } else if (fields.includes("e164")) {
        setNumberError({ text: (tr) => tr("whatsappSendingSettings.dialog.numberInvalid") });
        numberRef.current?.focus();
      } else if (error instanceof ApiRequestError && error.code === "whatsapp_number_taken" && error.details?.field === "agileCode") {
        agile.setCodeError({ text: (tr) => codeTakenText(tr, error) });
        codeRef.current?.focus();
      } else if (error instanceof ApiRequestError && error.code === "whatsapp_number_taken") {
        setNumberError({ text: (tr) => numberTakenText(tr, error) });
        numberRef.current?.focus();
      } else if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // L'elenco si rilegge (`useSetWhatsAppSender`) e il prossimo «Salva» porta la versione di adesso, quella che il server ha
        // detto (`details.version`, `null` se la persona non ne ha più uno), con il numero scritto qui.
        const current = error.details?.version;
        baseVersion.current = typeof current === "number" ? current : current === null ? null : (person.sender?.version ?? null);
        const name = person.displayName;
        setProblem({ text: (tr) => tr("whatsappSendingSettings.dialog.conflict", { name }) });
      } else {
        setProblem({ text: (tr) => messageForError(tr, error) });
      }
    }
  }

  /** «Salva» o «Registra su Meta e salva»; `resume` è «Riprova» dopo una registrazione non riuscita. */
  function submit(event?: FormEvent, resume = false) {
    event?.preventDefault();
    if (busy) return;
    const e164 = validNumber();
    if (e164 === null) return;
    if (channel === "meta") {
      void meta.start(e164, { resume });
    } else if (channel === "agile") {
      const code = agile.codeForSave();
      if (code !== null) void saveSender(e164, code);
    } else {
      void saveSender(e164);
    }
  }

  function reopenMetaWindow() {
    if (busy) return;
    const e164 = validNumber();
    if (e164 !== null) void meta.reopen(e164);
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("whatsappSendingSettings.dialog.title", { name: person.displayName })}
      onClose={close}
      footer={
        <>
          <Button onClick={close} disabled={!closable}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" type="submit" form={formId} disabled={busy} aria-busy={busy ? "true" : undefined}>
            {t(SAVE_LABEL[channel])}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={submit} aria-busy={busy} noValidate>
        <fieldset className="g-wa-modes">
          <legend>{t("whatsappSendingSettings.dialog.legend")}</legend>
          {WHATSAPP_CHANNEL_ORDER.map((option) => {
            const id = `${ids}-${option}`;
            const block = blockOf(option);
            let why: string | null = null;
            if (block === "next_release") why = t("whatsappSendingSettings.company.nextRelease");
            else if (block === "meta_unavailable") why = t("whatsappSendingSettings.company.metaUnavailable");
            else if (block === "agile_unavailable") why = t("whatsappSendingSettings.company.agileUnavailable");
            else if (block === "not_used") why = t("whatsappSendingSettings.dialog.notUsed", { organization });
            else if (block === "not_allowed") why = t("errors.whatsapp_channel_not_enabled");
            return (
              <label key={option} className="g-wa-mode" data-disabled={block === null ? undefined : "true"}>
                <input
                  type="radio"
                  name={`${ids}-channel`}
                  value={option}
                  checked={option === channel}
                  disabled={block !== null}
                  onChange={() => {
                    if (busy) return;
                    setChosen(option);
                    setNumberError(null);
                    setProblem(null);
                    agile.setCodeError(null);
                  }}
                  aria-labelledby={`${id}-name`}
                  aria-describedby={why ? `${id}-why ${id}-text` : `${id}-text`}
                />
                <ChannelText id={id} channel={option} why={why} text={t(`whatsappSendingSettings.channels.${option}.choice`)} />
              </label>
            );
          })}
        </fieldset>
        <TextField
          ref={numberRef}
          id={`${ids}-number`}
          label={channel === "manual" ? t("whatsappSendingSettings.dialog.number") : t("whatsappSendingSettings.dialog.numberMeta")}
          type="tel"
          autoComplete="off"
          placeholder={t("whatsappSendingSettings.dialog.placeholder")}
          hint={t("whatsappSendingSettings.dialog.numberHint")}
          error={numberError?.text(t)}
          value={number}
          onChange={(event) => {
            setNumber(event.target.value);
            setNumberError(null);
          }}
          // Mentre salva il campo resta leggibile e raggiungibile: un rifiuto del server ci riporta il cursore.
          readOnly={busy}
        />
        {channel === "meta" ? (
          <>
            <MetaRegistrationNote connection={company?.metaSignup.connection ?? null} />
            <MetaSenderStatus
              person={person}
              phase={meta.phase}
              busy={busy}
              current={<SenderState sender={person.sender} />}
              onRetry={() => submit(undefined, true)}
              onReopen={reopenMetaWindow}
            />
          </>
        ) : channel === "agile" ? (
          <AgileSetupFields setup={agile} codeRef={codeRef} id={`${ids}-code`} busy={busy} saving={save.isPending} />
        ) : (
          <p className="g-wa-muted">{t("whatsappSendingSettings.dialog.manualNote")}</p>
        )}
        <p>
          <strong>{t("whatsappSendingSettings.dialog.effect")}</strong> {effect}
        </p>
        {problem ? (
          <Reason tone="warning" role="alert">
            {problem.text(t)}
          </Reason>
        ) : null}
      </form>
    </Dialog>
  );
}

type ChannelTextProps = { id: string; channel: WhatsAppChannelCode; why: string | null; text: string; extra?: string };

/**
 * Il nome di un WhatsApp con il suo segno («consigliato», «come in GioiaFlow», «come oggi»), il perché di una scelta spenta
 * («Arriva con il prossimo rilascio», o che l'azienda non lo usa) e la sua riga: lo usano la finestra «Imposta» e le tre caselle
 * dell'azienda. `id` dà i nomi agli elementi che il controllo legge (`-name`, `-why` quando c'è un perché, `-text`).
 */
export function ChannelText({ id, channel, why, text, extra }: ChannelTextProps) {
  const t = useT();
  return (
    <span className="g-wa-mode__body">
      <span className="g-wa-mode__head">
        <strong id={`${id}-name`}>{t(`whatsappSendingSettings.channels.${channel}.label`)}</strong>
        <Chip tone={channel === "meta" ? "accent" : "neutral"}>{t(`whatsappSendingSettings.channels.${channel}.tag`)}</Chip>
        {why === null ? null : (
          <span id={`${id}-why`} className="g-wa-mode__later">
            <Icon name="clock" />
            {why}
          </span>
        )}
      </span>
      <span id={`${id}-text`} className="g-wa-mode__text">
        {text}
        {extra ? (
          <>
            {" "}
            {extra}
          </>
        ) : null}
      </span>
    </span>
  );
}
